import {useRouter} from 'next/router';
import {ReactNode, useCallback} from 'react';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {useDialog} from '../../../dialog/useDialog.hook';
import {showToast} from '../../../toast/toastHandler';
import {useLogin} from '../../../user/useLogin.hook';
import {ASK_PROJECT_ID, AskType} from '../constant';
import useCreateAskMutationHook from '../hook/useCreateAskMutationHook';
import {AskRequestProps} from '../presentational/AskRequest';
import {FormFieldSchemaType} from '../schema/AskRequestSchema';
import {getAskSubCategory} from '../util/getSubCategory';

interface AskRequestContainerProps {
  children: (props: AskRequestProps) => ReactNode;
  initialAskType?: AskType;
}

export function AskRequestContainer({
  children,
  initialAskType,
}: AskRequestContainerProps) {
  const router = useRouter();
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const {isLogin, user} = useLogin();
  const createAskMutation = useCreateAskMutationHook();
  const isGlobal = requestContext.requestInfo.isGlobal;
  const serviceLangUpperCase =
    requestContext.requestInfo.lang.serviceLang.toUpperCase();

  const initialDefaultValues: Partial<FormFieldSchemaType> = {
    projectId: isGlobal ? ASK_PROJECT_ID.PageGlobal : ASK_PROJECT_ID.Page,
    sendEmail: user?.email ?? undefined,
    loginId: user?.email ?? undefined,
    customerName: user?.nickname ?? undefined,
    appVersion: !requestContext.requestInfo?.display.isWebView
      ? `${requestContext.requestInfo?.browserName} / ${requestContext.requestInfo?.browserVersion}`
      : typeof window !== 'undefined'
        ? (window.KPApp?.getAppVersion?.() ?? '')
        : '',
    osVersion: requestContext.requestInfo?.display.isWebView
      ? `${requestContext.requestInfo?.osName}${requestContext.requestInfo?.osVersion}`
      : '', //  브라우저의 UA Freezing 정책으로 OS 정보가 Android 10; K, MAC 10.15.7 로 고정되기 때문에 고객센터측 혼란을 줄이기 위해 아예 정보를 보내지 않습니다(기획 확인 완료)
    deviceModel: requestContext.requestInfo?.display.isWebView
      ? requestContext.requestInfo?.deviceModel
      : '', //  브라우저의 UA Freezing
  };

  const {DialogWrapper, Dialog, hideDialog, showDialog} = useDialog({
    isMobile,
  });

  const onClickMyAsk = useCallback(() => {
    router.push('/ask?tab=my');
  }, [router]);

  const transformFormData = useCallback(
    (data: FormData) => {
      // @TODO: 추후 국가 코드 등이 포함되어 있을 때에 대한 유효성체크 및 파싱 로직 추가가 필요합니다. 필요하다면 기획과 스펙 논의 및 수정이 필요합니다.
      // 전화번호 정리
      const telNum = String(data.get('telNum') || '');
      if (telNum) {
        data.set('telNum', telNum.replace(/[^0-9]/g, ''));
      }

      // orderNo 변환
      const orderNoRequired = String(data.get('orderNoRequired') || '');
      if (orderNoRequired) {
        data.set('orderNo', orderNoRequired);
        data.delete('orderNoRequired');
      }

      // 서비스 언어 설정
      // @NOTE: 원래 휴대폰에 넣는 countryCode 지만 TCK 개발일정 이슈로 글로벌 문의 처리를 위해 콘텐츠 언어 값으로 사용합니다.
      const serviceLang =
        !isGlobal || serviceLangUpperCase === 'KO'
          ? 'KR'
          : (serviceLangUpperCase ?? 'TH');
      data.set('countryCode', serviceLang);

      // 글로벌 거주국가 설정
      const geoIp = isLogin
        ? requestContext.requestInfo.countryCode
        : requestContext.requestInfo.appInit.geoIp;
      data.set('residenceCountry', geoIp || 'XX');

      return data;
    },
    [isGlobal, serviceLangUpperCase, isLogin]
  );

  const onSubmit = async (data: FormData) => {
    const transformedData = transformFormData(data);

    try {
      await createAskMutation.mutateAsync(transformedData);
      showDialog();
    } catch {
      handleSubmitError();
    }
  };

  const handleSubmitError = useCallback(() => {
    showToast({
      message: t('more_customer_cs_inquiry_tab_save_fail_toast'),
      isMobile: requestContext.requestInfo.display.isMobile,
      delayMs: 2000,
    });
  }, []);

  return (
    <>
      {children({
        isWebView,
        onSubmit,
        initialAskType,
        initialAskSubCategory: getAskSubCategory(),
        initialDefaultValues,
        isLogin,
        isLoading: createAskMutation.isPending,
      })}

      <DialogWrapper data-test="ask-request-success-dialog">
        <Dialog.Dimmed />
        <Dialog.Title>
          {t('more_customer_cs_inquiry_tab_save_popup_title')}
        </Dialog.Title>
        <Dialog.List>
          <Dialog.Label>
            {t('more_customer_cs_inquiry_tab_save_popup_info')}
          </Dialog.Label>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={isLogin ? onClickMyAsk : hideDialog}>
            {isLogin
              ? t('more_customer_cs_inquiry_tab_save_popup_history_button')
              : t('more_customer_cs_inquiry_tab_save_popup_confirm_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    </>
  );
}
